import _ from 'lodash';

// The legacy Gauge chart definition a Gauge block saves as and draws with
// (components/Gauge). Named after its metric unless it's given another name.
const buildGaugeDefinition = ({
  card,
  id,
  name,
  metricDisplayName,
}: {
  card: Pick<Studio.Card, 'metricIds' | 'description' | 'gauge'>;
  id: string;
  name: string;
  // The gauge's label for its metric
  metricDisplayName: string;
}):
  | { chartDefinition: VisualisationDefinitions.Gauge }
  | { missing: string } => {
  const [metricId] = card.metricIds ?? [];
  if (!metricId) {
    return { missing: 'Add a metric' };
  }
  const { peerGroup, entity, isReversed } = card.gauge ?? {};
  if (!peerGroup || !entity) {
    return { missing: 'Choose a peer group and an entity to show a Gauge' };
  }
  return {
    chartDefinition: {
      id,
      name,
      description: card.description ?? '',
      type: 'Gauge',
      metricId,
      metricDisplayName,
      peerGroup,
      entity,
      ...(isReversed ? { isReversed: true } : {}),
    },
  };
};

// A Gauge block's settings from a saved Gauge
export const guessGaugeCard = (
  chartDef: VisualisationDefinitions.Gauge,
): Studio.GadgetSidecarCard => ({
  visType: 'gauge',
  metricIds: [chartDef.metricId],
  name: chartDef.name,
  ...(chartDef.description ? { description: chartDef.description } : {}),
  gauge: _.omitBy(
    {
      peerGroup: chartDef.peerGroup,
      entity: chartDef.entity,
      isReversed: chartDef.isReversed || undefined,
    },
    _.isUndefined,
  ),
});

// Whether the settings rebuild the saved Gauge. Its name and metric label
// are the gadget's own (the classic editor can change them), as is anything
// it keeps for soft deletes.
export const reproducesGauge = ({
  card,
  chartDef,
}: {
  card: Studio.GadgetSidecarCard;
  chartDef: VisualisationDefinitions.Gauge;
}): boolean => {
  if (card.visType !== 'gauge') {
    return false;
  }
  const built = buildGaugeDefinition({
    card,
    id: chartDef.id,
    name: chartDef.name,
    metricDisplayName: chartDef.metricDisplayName,
  });
  const comparable = (def: VisualisationDefinitions.Gauge) =>
    _.omitBy(
      {
        ..._.omit(def, ['isHidden', 'hiddenOn']),
        description: def.description || undefined,
        isReversed: def.isReversed || undefined,
      },
      _.isUndefined,
    );
  return (
    'chartDefinition' in built &&
    _.isEqual(comparable(built.chartDefinition), comparable(chartDef))
  );
};

export default buildGaugeDefinition;
